// T035 — Statsig implementation of AnalyticsProvider interface.
// Wraps the StatsigClient to conform to the vendor-neutral analytics API.

import { StableID, StatsigClient } from '@statsig/js-client';
import { runStatsigAutoCapture } from '@statsig/web-analytics';
import { beginGateValuesUpdate, endGateValuesUpdate } from '@/lib/analytics/gate-values';
import { logger } from '@/lib/telemetry/logger';
import type { AnalyticsConfig, AnalyticsProvider, ExperimentResult } from '../../types';

export class StatsigAnalyticsProvider implements AnalyticsProvider {
  private client: StatsigClient | null = null;
  private sdkKey = '';
  private readonly unavailableGateReasons = new Set<string>();
  private readonly missingTradeTraceEvents = new Set<string>();

  async initialize(config: AnalyticsConfig): Promise<void> {
    if (!config.clientKey) {
      return;
    }

    this.sdkKey = config.clientKey;
    this.client = new StatsigClient(
      config.clientKey,
      withStableId(config.clientKey, {
        userID: config.userId ?? '',
        custom: { platform: 'web' },
      }),
      {
        environment: { tier: config.environment },
        // Share the anonymous assignment with server-rendered Smart App Banners.
        enableCookies: true,
      },
    );
    await this.client.initializeAsync();
    // @ts-expect-error REASON: @statsig types incompatible with exactOptionalPropertyTypes (library bug)
    runStatsigAutoCapture(this.client, {
      consoleLogAutoCaptureSettings: { enabled: true, logLevel: 'error' },
    });
    void startSessionReplayAfterLoad(this.client);
  }

  checkGate(name: string): boolean {
    if (!this.client) {
      return false;
    }
    // Inspect without logging first. Statsig treats an exposure with an
    // unavailable assignment reason as a Group Assignment Health failure,
    // even though the feature was never served.
    const gate = this.client.getFeatureGate(name, { disableExposureLog: true });
    // Return `false` for any state where Statsig hasn't given us a real value:
    // - `*:Unrecognized` — gate doesn't exist in the Statsig dashboard
    // - `NoValues` / `Loading` / `Uninitialized` / `Error` — init didn't load values
    //   (e.g. network failure to featureassets.org / prodregistryv2.org)
    // Unknown gates correctly collapse to `false` for every user. Explicit
    // DevToolkit overrides are applied by `lib/analytics/index.ts:checkGate`.
    const reason = gate.details.reason;
    if (
      reason.endsWith('Unrecognized') ||
      reason === 'NoValues' ||
      reason === 'Loading' ||
      reason === 'Uninitialized' ||
      reason === 'Error'
    ) {
      const unavailableKey = `${name}:${reason}`;
      if (!this.unavailableGateReasons.has(unavailableKey)) {
        this.unavailableGateReasons.add(unavailableKey);
        logger.warn('Statsig gate evaluation unavailable', { gateName: name, reason });
      }
      return false;
    }
    // Record the exposure only after the assignment is known to be usable.
    return this.client.getFeatureGate(name).value;
  }

  getConfig(name: string): Record<string, unknown> {
    if (!this.client) {
      return {};
    }
    const config = this.client.getDynamicConfig(name);
    return config.value;
  }

  getExperiment(name: string): ExperimentResult {
    if (!this.client) {
      return { value: null, ruleID: '' };
    }
    const experiment = this.client.getExperiment(name);
    return { value: experiment.value, ruleID: experiment.ruleID };
  }

  trackEvent(name: string, properties: Record<string, unknown>): void {
    if (!this.client) {
      return;
    }
    // Statsig logEvent metadata accepts Record<string, string>; convert values.
    const metadata: Record<string, string> = {};
    for (const [key, val] of Object.entries(properties)) {
      metadata[key] = String(val);
    }
    // Event Value is Statsig's per-event funnel unit. Reuse the submission's
    // trace rather than changing the user's identity or counting users.
    this.client.logEvent(name, this.tradeFunnelValue(name, properties), metadata);
  }

  identifyUser(userId: string, attributes: Record<string, unknown>): void {
    if (!this.client) {
      return;
    }
    // Statsig switches identity immediately, loads that user's cached values,
    // and fetches fresh evaluations. Its initiator guard discards responses
    // superseded by a later identify, including a richer same-account identity.
    // Keep this synchronous for auth events emitted immediately after identify;
    // queuing updates behind the network would attribute those events too late.
    // Gate consumers, however, must not commit to the anonymous evaluation while
    // the identified user's values are in flight — signal pending → settled.
    beginGateValuesUpdate();
    this.client
      .updateUserAsync(withStableId(this.sdkKey, statsigUserFromIdentity(userId, attributes)))
      .catch((error: unknown) => {
        logger.warn('Statsig identity update failed — gate values stay at last evaluation', {
          error: error instanceof Error ? error.message.slice(0, 200) : String(error),
        });
      })
      .finally(endGateValuesUpdate);
  }

  shutdown(): void {
    if (this.client) {
      this.client.shutdown();
      this.client = null;
    }
  }

  // ALLOW-UNKNOWN: analytics provider boundary accepts arbitrary event metadata.
  private tradeFunnelValue(name: string, properties: Record<string, unknown>): string | undefined {
    if (
      ![
        'trade_attempted',
        'trade_success',
        'trade_error',
        'trade_cancelled',
        'trade_unknown',
      ].includes(name)
    )
      return undefined;
    const instruction = properties.instruction;
    if (instruction !== 'buy' && instruction !== 'sell' && instruction !== 'swap') return undefined;
    const traceId = properties.traceId;
    if (typeof traceId === 'string' && traceId.trim() !== '') return traceId;
    if (!this.missingTradeTraceEvents.has(name)) {
      this.missingTradeTraceEvents.add(name);
      logger.warn('analytics.trade_trace_missing', { eventName: name });
    }
    return undefined;
  }
}

const STATSIG_TOP_LEVEL_KEYS = ['email', 'country', 'locale', 'appVersion'] as const;
type StatsigTopLevelKey = (typeof STATSIG_TOP_LEVEL_KEYS)[number];

function isStatsigTopLevelKey(key: string): key is StatsigTopLevelKey {
  return (STATSIG_TOP_LEVEL_KEYS as readonly string[]).includes(key);
}

/**
 * Split incoming attributes into Statsig's first-class user fields vs. the
 * catch-all `custom` map. First-class fields drive built-in dashboard
 * filters (Country, Email, …). `platform` is always `web` so web vs mobile
 * segmentation cannot be overwritten by a caller attribute.
 */
function statsigUserFromIdentity(
  userId: string,
  attributes: Record<string, unknown>,
): {
  userID: string;
  email?: string;
  country?: string;
  locale?: string;
  appVersion?: string;
  custom: Record<string, string | number | boolean>;
} {
  const topLevel: Partial<Record<StatsigTopLevelKey, string>> = {};
  const custom: Record<string, string | number | boolean> = {};
  for (const [key, val] of Object.entries(attributes)) {
    if (val === null || val === undefined) continue;
    if (isStatsigTopLevelKey(key) && typeof val === 'string') {
      topLevel[key] = val;
      continue;
    }
    if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
      custom[key] = val;
    } else {
      custom[key] = String(val);
    }
  }
  custom.platform = 'web';
  return { userID: userId, ...topLevel, custom };
}

/**
 * Statsig evaluates a stableID-typed gate only when the user object itself
 * carries `customIDs.stableID`. The SDK generates and persists the id but
 * never attaches it to the user, so without this every stableID gate
 * (`web_region`, `web_rewards_platform`, …) came back
 * `Unrecognized` and read as `false` for every session. `null` means stable
 * IDs are disabled for this key; the user is then passed through unchanged.
 */
function withStableId<T extends object>(sdkKey: string, user: T): T {
  const stableID = StableID.get(sdkKey);
  return stableID === null ? user : { ...user, customIDs: { stableID } };
}

async function startSessionReplayAfterLoad(client: StatsigClient): Promise<void> {
  await waitForWindowLoad();
  try {
    const { runStatsigSessionReplay } = await import('@statsig/session-replay');
    // @ts-expect-error REASON: @statsig types incompatible with exactOptionalPropertyTypes (library bug)
    runStatsigSessionReplay(client);
  } catch (error: unknown) {
    logger.warn('Statsig session replay initialization failed', {
      error: error instanceof Error ? error.message.slice(0, 200) : String(error),
    });
  }
}

function waitForWindowLoad(): Promise<void> {
  if (document.readyState === 'complete') {
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    window.addEventListener('load', () => resolve(), { once: true });
  });
}

export function createStatsigProvider(): AnalyticsProvider {
  return new StatsigAnalyticsProvider();
}
